import { useRef } from 'react';
import { useBuilder } from '../../context/BuilderContext';
import { useEditorCapability } from '../../hooks/useEditorCapability';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useT } from '../../hooks/useT';
import styles from './EditorSheet.module.css';

const DRAG_THRESHOLD = 40;

/**
 * The mobile shell for the editor.
 *
 * Desktop keeps the fixed right-hand column it always had; only below the
 * breakpoint does this become a snapping bottom sheet. The sheet is anchored
 * bottom: 0 and its `full` snap is expressed as a top offset rather than a
 * height, so a wrong --fgl-vh costs space at the top instead of pushing the
 * peek bar's controls off the bottom of the screen.
 */
function EditorSheet({ peekBar, tabsBar, children }) {
  const isMobile = useIsMobile();
  const { sheetSnap, setSheetSnap, cycleSheetSnap } = useBuilder();
  const { keyboardInset } = useEditorCapability();
  const t = useT();
  const dragStartY = useRef(null);
  // Set once a drag crosses the threshold, so the click a browser
  // synthesizes right after pointerup on the same element does not also
  // cycle the snap and silently undo the drag (review Finding 2).
  const wasDragging = useRef(false);

  if (!isMobile) {
    return (
      <aside className={styles.desktopPanel} aria-label={t('editor.panelLabel', 'Editor')}>
        {peekBar}
        {tabsBar && <div className={styles.tabsBar}>{tabsBar}</div>}
        <div className={styles.body}>{children}</div>
      </aside>
    );
  }

  // Mouse and pen pointers do not get implicit capture the way touch does.
  // A real drag travels DRAG_THRESHOLD (40px) vertically, well outside the
  // handle's 24px hit box, so without capture pointerup fires on whatever
  // element is under the cursor instead of the handle (review Finding 1).
  // Guarded because this project's Jest environment (jsdom 16) has no
  // Pointer Events support at all.
  const capturePointer = (e) => {
    if (typeof e.currentTarget.setPointerCapture === 'function') {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const releasePointer = (e) => {
    const target = e.currentTarget;
    if (typeof target.releasePointerCapture !== 'function') return;
    if (typeof target.hasPointerCapture === 'function' && !target.hasPointerCapture(e.pointerId)) return;
    target.releasePointerCapture(e.pointerId);
  };

  const handlePointerDown = (e) => {
    dragStartY.current = e.clientY;
    capturePointer(e);
  };

  const handlePointerUp = (e) => {
    const start = dragStartY.current;
    dragStartY.current = null;
    releasePointer(e);
    if (start === null) return;

    const delta = e.clientY - start;
    if (Math.abs(delta) < DRAG_THRESHOLD) return;

    wasDragging.current = true;

    if (delta < 0) {
      setSheetSnap(sheetSnap === 'peek' ? 'half' : 'full');
    } else {
      setSheetSnap(sheetSnap === 'full' ? 'half' : 'peek');
    }
  };

  // An OS-cancelled gesture (e.g. an incoming call) never reaches
  // pointerup, so dragStartY must be reset here too or the next unrelated
  // pointerup would read a stale start position.
  const handlePointerCancel = (e) => {
    dragStartY.current = null;
    releasePointer(e);
  };

  const handleClick = () => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    cycleSheetSnap();
  };

  return (
    <aside
      className={styles.sheet}
      data-snap={sheetSnap}
      data-testid="editor-sheet"
      aria-label={t('editor.panelLabel', 'Editor')}
      style={{ '--fgl-keyboard-inset': `${keyboardInset}px` }}
    >
      <button
        type="button"
        className={styles.handle}
        aria-label={t('editor.expandCollapse', 'Expand or collapse the editor')}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <span className={styles.handleGrip} />
      </button>
      {peekBar}
      {tabsBar && <div className={styles.tabsBar}>{tabsBar}</div>}
      <div className={styles.body}>{children}</div>
    </aside>
  );
}

export default EditorSheet;
